import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Moon, CheckCircle2, Sparkles, Flame, ListChecks } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { Field } from '@/components/FormControls';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useLang } from '@/context/LanguageContext';

export default function DailyReview() {
  const { t, lang } = useLang();
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ completedTasks: '', reflection: '', difficulty: '', tomorrowPriority: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const QUESTIONS = [
    { key: 'completedTasks', label: t('review.qCompletedLabel'), placeholder: t('review.qCompletedPlaceholder') },
    { key: 'reflection', label: t('review.qReflectionLabel'), placeholder: t('review.qReflectionPlaceholder') },
    { key: 'difficulty', label: t('review.qDifficultyLabel'), placeholder: t('review.qDifficultyPlaceholder') },
    { key: 'tomorrowPriority', label: t('review.qTomorrowLabel'), placeholder: t('review.qTomorrowPlaceholder') },
  ];

  const load = useCallback(async () => {
    try {
      const [s, tr] = await Promise.all([api.get('/reviews/summary'), api.get('/reviews/today')]);
      setSummary(s.data);
      if (tr.data && tr.data.id) {
        setForm({
          completedTasks: tr.data.completedTasks || '',
          reflection: tr.data.reflection || '',
          difficulty: tr.data.difficulty || '',
          tomorrowPriority: tr.data.tomorrowPriority || '',
        });
        setSaved(true);
      }
    } catch (e) {
      toast.error(t('review.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/reviews', form);
      toast.success(t('review.saved'));
      setSaved(true);
    } catch (e) {
      toast.error(t('review.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !summary) return <FullScreenLoader label={t('review.loader')} />;

  return (
    <AppLayout>
      <AppHeader title={t('review.title')} subtitle={t('review.subtitle')} back />

      <div className="mt-3 grid grid-cols-2 gap-3">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-4">
          <div className="flex items-center gap-2 text-emerald-300"><ListChecks className="h-4 w-4" /><span className="text-[11px] text-foreground/55">{t('review.tasksDone')}</span></div>
          <p className="mt-2 font-display text-2xl font-semibold text-foreground">{summary.completedTasksCount}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass-card rounded-2xl p-4">
          <div className="flex items-center gap-2 text-amber-300"><Flame className="h-4 w-4" /><span className="text-[11px] text-foreground/55">{t('review.habitsDone')}</span></div>
          <p className="mt-2 font-display text-2xl font-semibold text-foreground">{summary.completedHabitsCount}/{summary.totalHabits}</p>
        </motion.div>
      </div>

      <div className="mt-3 rounded-2xl border border-brand-cyan/20 bg-brand-cyan/[0.08] p-4">
        <div className="flex items-center gap-2 text-brand-cyan"><Sparkles className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">{t('review.tomorrowLabel')}</span></div>
        <p className="mt-1.5 text-sm leading-6 text-foreground/90">{summary.tomorrowFocus}</p>
      </div>

      <div data-testid="daily-review-form" className="mt-5 space-y-4">
        {QUESTIONS.map((q) => (
          <Field key={q.key} label={q.label}>
            <Textarea
              data-testid={`review-${q.key}-input`}
              value={form[q.key]}
              onChange={(e) => { setForm({ ...form, [q.key]: e.target.value }); setSaved(false); }}
              placeholder={q.placeholder}
              className="min-h-[64px] rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40"
            />
          </Field>
        ))}
      </div>

      {saved ? (
        <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-emerald-300/20 bg-emerald-400/[0.08] py-3 text-sm font-medium text-emerald-200">
          <CheckCircle2 className="h-4 w-4" /> {t('review.savedBanner')}
        </div>
      ) : null}

      <Button
        data-testid="daily-review-submit-button"
        onClick={save}
        disabled={saving}
        className="btn-brand mt-4 h-12 w-full rounded-xl text-base font-semibold text-brand-ink"
      >
        <Moon className="mr-1.5 h-4 w-4" /> {saved ? t('review.update') : t('review.save')}
      </Button>
    </AppLayout>
  );
}
